import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Globe, Check } from 'lucide-react'
import { api } from '../services/api'
import { useLanguageStore } from '../stores/languageStore'

export default function LanguageSwitcher() {
  const {
    currentLanguageId,
    setCurrentLanguage,
    initializeLanguage,
    _hasHydrated,
  } = useLanguageStore()

  const {
    data: languagesData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const res = await api.get('/languages')
      const data = res.data.data || res.data
      return Array.isArray(data) ? data : []
    },
  })

  const languages = Array.isArray(languagesData)
    ? languagesData
    : Array.isArray(languagesData?.data)
    ? languagesData.data
    : []

  useEffect(() => {
    if (_hasHydrated && languages.length > 0 && !currentLanguageId) {
      initializeLanguage(languages)
    }
  }, [languages, currentLanguageId, initializeLanguage, _hasHydrated])

  if (!_hasHydrated) {
    return null
  }

  if (!currentLanguageId) {
    return null
  }

  if (isLoading || isFetching || languages.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700">
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline">Loading...</span>
      </div>
    )
  }

  const currentLanguage =
    languages.find((l) => l.id === currentLanguageId) || languages[0]

  return (
    <div className="relative group">
      <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
        <Globe className="h-4 w-4" />
        <span>{currentLanguage?.flag}</span>
        <span className="hidden sm:inline">{currentLanguage?.name}</span>
      </button>

      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
        <div className="py-1">
          {languages
            .filter((l) => l.is_active)
            .map((language) => (
              <button
                key={language.id}
                onClick={() => setCurrentLanguage(language.id, language.code)}
                className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span>{language.flag}</span>
                  <span>{language.name}</span>
                </div>
                {currentLanguageId === language.id && (
                  <Check className="h-4 w-4 text-primary-600" />
                )}
              </button>
            ))}
        </div>
      </div>
    </div>
  )
}
